import { statusAPI } from '@/lib/api';
import { isNative } from '@/services/platform';

class GeolocationService {
  constructor() {
    this.watchId = null;
    this.isTracking = false;
    this.lastPosition = null;
    this.updateInterval = null;
    this.errorCallback = null;
    this.statusCallback = null;
    this.wakeLock = null;
    this.wakeLockInterval = null;
    this.visibilityHandler = null;
    this.LOCATION_OPTIONS = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 10000
    };
    this.UPDATE_INTERVAL_MS = 30000; // 30 seconds
    this.nativeGeoModule = null;
  }

  async getNativeGeo() {
    if (!this.nativeGeoModule) {
      this.nativeGeoModule = await import('@/services/nativeGeolocation');
    }
    return this.nativeGeoModule;
  }

  isAvailable() {
    return 'geolocation' in navigator;
  }

  supportsWakeLock() {
    return 'wakeLock' in navigator;
  }

  async checkPermission() {
    if (isNative()) {
      try {
        const nativeGeo = await this.getNativeGeo();
        if (typeof nativeGeo.checkPermission === 'function') {
          return await nativeGeo.checkPermission();
        }
      } catch {
        return 'unknown';
      }
      return 'unknown';
    }
    if (!navigator.permissions) return 'unknown';
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state;
    } catch {
      return 'unknown';
    }
  }

  async requestPermission() {
    if (isNative()) {
      const nativeGeo = await this.getNativeGeo();
      if (typeof nativeGeo.requestPermission === 'function') {
        return nativeGeo.requestPermission();
      }
      return 'unknown';
    }
    return new Promise((resolve, reject) => {
      if (!this.isAvailable()) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, this.LOCATION_OPTIONS);
    });
  }

  // Acquire Wake Lock — prevents screen from sleeping (Android Chrome)
  async acquireWakeLock() {
    if (!this.supportsWakeLock()) return;
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', () => {
        // Wake lock released (e.g. tab hidden) — re-acquire when visible again
        this.wakeLock = null;
      });
    } catch (err) {
      // Wake lock not critical — silently fail
      console.warn('Wake Lock not acquired:', err.message);
    }
  }

  // Release Wake Lock
  async releaseWakeLock() {
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
      } catch { /* ignore */ }
      this.wakeLock = null;
    }
    if (this.wakeLockInterval) {
      clearInterval(this.wakeLockInterval);
      this.wakeLockInterval = null;
    }
  }

  // Set up visibility change handler — re-acquire wake lock when tab comes back
  setupVisibilityHandler() {
    this.visibilityHandler = async () => {
      if (document.visibilityState === 'visible' && this.isTracking) {
        // Tab is visible again — re-acquire wake lock
        await this.acquireWakeLock();
        // Immediately send a position update to refresh status
        if (this.lastPosition) {
          this.sendStatusUpdate(this.lastPosition);
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  removeVisibilityHandler() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  // Start continuous tracking
  async startTracking(onStatusUpdate, onError) {
    // Native app: use Foreground Service (works with screen off, 8+ hours)
    if (isNative()) {
      this.statusCallback = onStatusUpdate;
      this.errorCallback = onError;
      this.isTracking = true;
      const nativeGeo = await this.getNativeGeo();
      return nativeGeo.startTracking(onStatusUpdate, onError);
    }

    // Web fallback: browser geolocation + wake lock
    if (!this.isAvailable()) {
      onError?.(new Error('Geolocation not available'));
      return false;
    }

    if (this.isTracking) return true;

    this.statusCallback = onStatusUpdate;
    this.errorCallback = onError;
    this.isTracking = true;

    // Acquire Wake Lock to prevent screen sleep
    await this.acquireWakeLock();

    // Re-acquire wake lock every 45 seconds (some browsers release it silently)
    this.wakeLockInterval = setInterval(async () => {
      if (this.isTracking && !this.wakeLock && document.visibilityState === 'visible') {
        await this.acquireWakeLock();
      }
    }, 45000);

    // Watch position changes
    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.handlePositionUpdate(position),
      (error) => this.handlePositionError(error),
      this.LOCATION_OPTIONS
    );

    // Periodic server sync every 30 seconds
    this.updateInterval = setInterval(() => {
      if (this.lastPosition) {
        this.sendStatusUpdate(this.lastPosition);
      }
    }, this.UPDATE_INTERVAL_MS);

    // Handle tab visibility changes
    this.setupVisibilityHandler();

    return true;
  }

  // Stop tracking
  async stopTracking() {
    // Native app: stop Foreground Service
    if (isNative()) {
      const nativeGeo = await this.getNativeGeo();
      await nativeGeo.stopTracking();
      this.isTracking = false;
      this.statusCallback = null;
      this.errorCallback = null;
      return;
    }

    // Web fallback
    // Notify backend that GPS tracking stopped — closes open time_entry
    await this.sendNoGpsStatus().catch(() => {});

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    await this.releaseWakeLock();
    this.removeVisibilityHandler();

    this.isTracking = false;
    this.lastPosition = null;
    this.statusCallback = null;
    this.errorCallback = null;
  }

  handlePositionUpdate(position) {
    const newPos = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp
    };
    const moved = this.lastPosition
      ? this._distance(this.lastPosition, newPos) > 20
      : true;
    this.lastPosition = newPos;
    if (moved) {
      this.sendStatusUpdate(newPos, this.extraData || {});
    }
  }

  _distance(a, b) {
    const R = 6371000;
    const dLat = (b.latitude - a.latitude) * Math.PI / 180;
    const dLon = (b.longitude - a.longitude) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2
      + Math.cos(a.latitude * Math.PI / 180)
      * Math.cos(b.latitude * Math.PI / 180)
      * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  handlePositionError(error) {
    let message = 'Location error';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = 'Location permission denied';
        break;
      case error.POSITION_UNAVAILABLE:
        message = 'Location unavailable';
        this.sendNoGpsStatus();
        break;
      case error.TIMEOUT:
        message = 'Location request timeout';
        // Don't treat timeout as fatal — just log and retry
        console.warn('GPS timeout — will retry on next interval');
        return;
      default:
        message = error.message;
    }
    this.errorCallback?.(new Error(message));
  }

  async sendStatusUpdate(position, extraData = {}) {
    try {
      const response = await statusAPI.update({
        latitude: position.latitude,
        longitude: position.longitude,
        ...extraData
      });
      this.statusCallback?.(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to send status update:', error);
      this.errorCallback?.(error);
      return null;
    }
  }

  async sendNoGpsStatus() {
    try {
      const response = await statusAPI.update({});
      this.statusCallback?.(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to send NO_GPS status:', error);
      return null;
    }
  }

  async getCurrentPosition() {
    // Native app
    if (isNative()) {
      const nativeGeo = await this.getNativeGeo();
      return nativeGeo.getCurrentPosition();
    }

    // Web fallback
    return new Promise((resolve, reject) => {
      if (!this.isAvailable()) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        }),
        (error) => reject(error),
        this.LOCATION_OPTIONS
      );
    });
  }

  async changeStatus(newStatus) {
    const position = this.lastPosition || await this.getCurrentPosition().catch(() => null);
    const payload = { new_status: newStatus };
    if (position) {
      payload.latitude = position.latitude;
      payload.longitude = position.longitude;
    }
    const response = await statusAPI.update(payload);
    this.statusCallback?.(response.data);
    return response.data;
  }
}

export const geolocationService = new GeolocationService();
export default geolocationService;
